import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Server, Shield, Bell, Database, Globe, Eye, EyeOff, Save } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { useToast } from '../../hooks/use-toast';
import { useSuperAdminAuth } from '../../contexts/SuperAdminAuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const ENV_VARS = [
  { label: 'Backend URL', value: BACKEND_URL },
  { label: 'Firebase Project', value: process.env.REACT_APP_FIREBASE_PROJECT_ID || '—' },
  { label: 'Firebase Auth Domain', value: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || '—' },
  { label: 'Superadmin API Base', value: process.env.REACT_APP_SUPERADMIN_API_BASE || '(derived)' },
];

const Section = ({ title, icon: Icon, children }) => (
  <Card className="bg-white shadow-md">
    <CardHeader className="pb-3">
      <CardTitle className="flex items-center gap-2 text-lg"><Icon className="h-5 w-5 text-purple-600" />{title}</CardTitle>
    </CardHeader>
    <CardContent>{children}</CardContent>
  </Card>
);

const SystemSettings = () => {
  const { toast } = useToast();
  const { superAdmin } = useSuperAdminAuth();
  const [showValues, setShowValues] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [registrationOpen, setRegistrationOpen] = useState(true);
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [smsNotifications, setSmsNotifications] = useState(true);
  const [otpExpiry, setOtpExpiry] = useState('5');
  const [sessionTimeout, setSessionTimeout] = useState('30');
  const [maxLoginAttempts, setMaxLoginAttempts] = useState('5');

  const apiRef = useRef(null);
  if (!apiRef.current) {
    const instance = axios.create({ baseURL: BACKEND_URL });
    instance.interceptors.request.use((config) => {
      const token = superAdmin?.token || localStorage.getItem('superadmin_session') ||
        localStorage.getItem('superadmin_session_token') || localStorage.getItem('superAdminToken') ||
        localStorage.getItem('superadminToken');
      if (token) { config.headers = config.headers || {}; config.headers.Authorization = `Bearer ${token}`; }
      return config;
    });
    apiRef.current = instance;
  }
  const api = apiRef.current;

  const applyDoc = (d) => {
    if (!d) return;
    setMaintenanceMode(!!d.maintenance_mode);
    setRegistrationOpen(d.open_registration !== false);
    setEmailNotifications(d.email_notifications !== false);
    setSmsNotifications(d.sms_notifications !== false);
    setOtpExpiry(String(d.otp_expiry_minutes ?? 5));
    setSessionTimeout(String(d.session_timeout_minutes ?? 30));
    setMaxLoginAttempts(String(d.max_login_attempts ?? 5));
  };

  const load = async () => {
    setLoading(true);
    try { const r = await api.get('/api/v1/super-admin/settings'); applyDoc(r.data); }
    catch { /* keep defaults */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Real persistence — PUT /super-admin/settings then reload saved values.
  const saveSection = async (label) => {
    setSaving(label);
    try {
      const payload = {
        maintenance_mode: maintenanceMode,
        open_registration: registrationOpen,
        email_notifications: emailNotifications,
        sms_notifications: smsNotifications,
        otp_expiry_minutes: Number(otpExpiry) || 5,
        session_timeout_minutes: Number(sessionTimeout) || 30,
        max_login_attempts: Number(maxLoginAttempts) || 5,
      };
      const r = await api.put('/api/v1/super-admin/settings', payload);
      applyDoc(r.data);
      toast({ title: 'Saved', description: `${label} settings persisted.` });
    } catch (err) {
      toast({ variant: 'destructive', title: 'Save failed', description: err?.response?.status ? `HTTP ${err.response.status}` : 'network error' });
    } finally {
      setSaving(null);
    }
  };

  const Toggle = ({ value, onChange, label, description }) => (
    <div className="flex items-center justify-between py-3 border-b last:border-0">
      <div>
        <div className="font-medium text-sm text-gray-800">{label}</div>
        {description && <div className="text-xs text-gray-500 mt-0.5">{description}</div>}
      </div>
      <button onClick={() => onChange(!value)} className={`relative w-11 h-6 rounded-full transition-colors focus:outline-none ${value ? 'bg-purple-600' : 'bg-gray-300'}`}>
        <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${value ? 'translate-x-5' : 'translate-x-0'}`} />
      </button>
    </div>
  );

  const SaveBtn = ({ label }) => (
    <Button size="sm" onClick={() => saveSection(label)} disabled={!!saving || loading} className="bg-purple-600 hover:bg-purple-700 text-white">
      <Save className="h-3.5 w-3.5 mr-1" />{saving === label ? 'Saving…' : 'Save'}
    </Button>
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">System Settings</h1>
        <p className="text-gray-600 mt-1">Platform configuration (persisted){loading ? ' — loading…' : ''}</p>
      </div>

      <Section title="Environment" icon={Server}>
        <div className="flex items-center justify-between mb-4">
          <span className="text-sm text-gray-600">Frontend build environment (read-only)</span>
          <div className="flex items-center gap-2">
            <Badge className={process.env.NODE_ENV === 'production' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>{process.env.NODE_ENV || 'development'}</Badge>
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setShowValues(!showValues)}>
              {showValues ? <EyeOff className="h-3.5 w-3.5 mr-1" /> : <Eye className="h-3.5 w-3.5 mr-1" />}{showValues ? 'Hide' : 'Show'}
            </Button>
          </div>
        </div>
        <div className="space-y-2">
          {ENV_VARS.map(({ label, value }) => (
            <div key={label} className="flex items-center justify-between py-2 border-b last:border-0">
              <span className="text-sm text-gray-600 font-medium">{label}</span>
              <span className="text-sm text-gray-800 font-mono truncate max-w-xs">{showValues ? value : (value && value !== '—' ? '••••••••' : '—')}</span>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Platform Controls" icon={Globe}>
        <Toggle value={maintenanceMode} onChange={setMaintenanceMode} label="Maintenance Mode" description="Prevents new logins and shows a maintenance page to end users" />
        <Toggle value={registrationOpen} onChange={setRegistrationOpen} label="Open Registration" description="Allow new businesses and customers to self-register" />
        <div className="pt-3 flex justify-end"><SaveBtn label="Platform" /></div>
      </Section>

      <Section title="Security Policy" icon={Shield}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div className="space-y-1"><Label htmlFor="otp-expiry" className="text-xs">OTP Expiry (minutes)</Label>
            <Input id="otp-expiry" type="number" min="1" max="60" value={otpExpiry} onChange={(e) => setOtpExpiry(e.target.value)} /></div>
          <div className="space-y-1"><Label htmlFor="session-timeout" className="text-xs">Session Timeout (minutes)</Label>
            <Input id="session-timeout" type="number" min="5" max="480" value={sessionTimeout} onChange={(e) => setSessionTimeout(e.target.value)} /></div>
          <div className="space-y-1"><Label htmlFor="max-attempts" className="text-xs">Max Login Attempts</Label>
            <Input id="max-attempts" type="number" min="1" max="20" value={maxLoginAttempts} onChange={(e) => setMaxLoginAttempts(e.target.value)} /></div>
        </div>
        <div className="flex justify-end"><SaveBtn label="Security" /></div>
      </Section>

      <Section title="Notification Channels" icon={Bell}>
        <Toggle value={emailNotifications} onChange={setEmailNotifications} label="Email Notifications" description="Send system alerts and reports via email" />
        <Toggle value={smsNotifications} onChange={setSmsNotifications} label="SMS Notifications" description="Send critical alerts via Twilio SMS" />
        <div className="pt-3 flex justify-end"><SaveBtn label="Notifications" /></div>
      </Section>

      <Section title="Data Layer" icon={Database}>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between py-2 border-b"><span className="text-gray-600">Primary Database</span><span className="text-gray-800 font-medium">MongoDB Atlas</span></div>
          <div className="flex justify-between py-2 border-b"><span className="text-gray-600">ODM Driver</span><span className="text-gray-800 font-medium">Motor (async)</span></div>
          <div className="flex justify-between py-2 border-b"><span className="text-gray-600">Auth System</span><span className="text-gray-800 font-medium">Firebase + OTP/JWT</span></div>
          <div className="flex justify-between py-2"><span className="text-gray-600">Deployment</span><span className="text-gray-800 font-medium">Google Cloud Run</span></div>
        </div>
      </Section>
    </div>
  );
};

export default SystemSettings;
